import axios, { type AxiosRequestConfig } from 'axios';

import { InvalidTokenError } from '@/errors';
import { useCurrentLocale } from '@/plugins/i18n';
import { useAuthStore } from '@/stores';

function setHeader(headers: Record<string, string> = {}): Record<string, string> {
  headers['Content-Type'] = 'application/json';

  const userToken = localStorage.getItem('accessToken');
  if (userToken) {
    headers['Authorization'] = `Bearer ${userToken}`;
  } else {
    throw new InvalidTokenError('access');
  }
  headers['Accept-Language'] = useCurrentLocale().value;

  return headers;
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response.status === 401) {
      useAuthStore().logout();
    } else if (error.response.status === 410) {
      location.reload();
    }
    return Promise.reject(error);
  },
);

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
  pagination?: Pagination;
}

export interface Pagination {
  currentPage: number;
  nextPage?: number | null;
  previousPage?: number | null;
  totalCount: number;
  totalPages: number;
}

export type ApiMethod = <T>(
  url: string,
  body?: any,
  type?: AxiosRequestConfig['responseType'],
  headers?: Record<string, string>,
) => Promise<ApiResponse<T>>;

export const getRequest: ApiMethod = async <T>(
  url: string,
  body?: any,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<T>> => {
  const config: AxiosRequestConfig = {
    method: 'get',
    url: url,
    headers: setHeader(headers),
    params: body,
    responseType: type,
  };

  return (await request(config)).data;
};

export const postRequest: ApiMethod = async <T>(
  url: string,
  body?: any,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<T>> => {
  const config: AxiosRequestConfig = {
    method: 'post',
    url: url,
    headers: setHeader(headers),
    data: body,
    responseType: type,
  };

  if (body instanceof FormData && config.headers) {
    delete config.headers['Content-Type'];
  }
  return (await request(config)).data;
};

export const putRequest: ApiMethod = async <T>(
  url: string,
  body?: any,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<T>> => {
  const config: AxiosRequestConfig = {
    method: 'put',
    url: url,
    headers: setHeader(headers),
    data: body,
    responseType: type,
  };
  return (await request(config)).data;
};

export const patchRequest: ApiMethod = async <T>(
  url: string,
  body?: any,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<T>> => {
  const config: AxiosRequestConfig = {
    method: 'patch',
    url: url,
    headers: setHeader(headers),
    data: body,
    responseType: type,
  };
  return (await request(config)).data;
};

export const deleteRequest: ApiMethod = async <T>(
  url: string,
  body?: any,
  type: AxiosRequestConfig['responseType'] = 'json',
  headers?: Record<string, string>,
): Promise<ApiResponse<T>> => {
  const config: AxiosRequestConfig = {
    method: 'delete',
    url: url,
    headers: setHeader(headers),
    data: body,
    responseType: type,
  };
  return (await request(config)).data;
};

export const getBlobRequest = async (url: string, type: AxiosRequestConfig['responseType'] = 'blob'): Promise<Blob> => {
  const config: AxiosRequestConfig = {
    method: 'get',
    url: url,
    headers: setHeader(),
    responseType: type,
  };

  const response = await axios(config);
  return response.data;
};

export const request = async (config: AxiosRequestConfig): Promise<any> => {
  try {
    return await axios(config);
  } catch (err: any) {
    console.error(err);
    throw err;
  }
};

export default {
  get: getRequest,
  post: postRequest,
  put: putRequest,
  patch: patchRequest,
  delete: deleteRequest,
  getBlobRequest,
};
